<script setup>
    
    import { useMediaQuery } from '@vueuse/core'
    const isTablet = useMediaQuery('(min-width: 768px)', { ssrWidth: 768 } )

    const props = defineProps({
        session: {
            type: Object,
            required: true
        }
    })

    const params = ref({ per_page: 3, page: 1, exclude: props.session.id })
    const sessionTags = []

    if (props.session?.related_sessions?.length) {
        params.value.id = props.session.related_sessions?.join(',')
    }
    
    if (props.session) {
        Object.keys(props.session).forEach(key => {
            if (key.startsWith('session_') && props.session[key].length) {
                sessionTags.push(...props.session[key])
            }
        })
    }
    
    if (sessionTags.length) {
        params.value.filter_tags = sessionTags.map(tag => tag.id).join(',')
    }

    const { data } = await useAsyncData(`related-${props.session.id}`, () => $fetch(`/api/v1/sessions`, { params: { ...params.value } }))

</script>

<template>
    <div class="block block-related-sessions" v-if="data.sessions?.length">
        <div class="container">
            <div class="header">
                <h2 class="h2">Related Sessions</h2>
                <div class="actions desktop">
                    <ProjectButton label="All Sessions" link="/sessions" outline></ProjectButton>
                </div>
            </div>
            <ul class='grid' v-if="data.sessions.length" :tabindex=" isTablet ? -1 : 0">
                <li v-for='session in data.sessions' :key='session.id' class='grid-item'>
                    <SessionsCard :session='session' layout='slim' show_sponsors show_tags hide_featured teal full sessionsUrl="/sessions" />
                </li>
            </ul>
            <div class="mobile actions">
                <ProjectButton label="All Sessions" link="/sessions" outline class="mobile"></ProjectButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .block.block-related-sessions {
        color: var(--cream);
        background-color: var(--teal);
        
        @media(min-width: $tablet) {
            background-image: url('@/assets/img/backgrounds/bubble-teal-12.png');
            background-repeat: no-repeat;
            background-position: top right 14.1875rem;
        }

        h2 {
            font-size: calc(58 / var(--base-fs) * 1rem);
            font-weight: 500;
            letter-spacing: -0.10875rem;
            line-height: 4rem;
        }

        .actions a:hover {
            color: var(--teal);
        }

        .actions.mobile {
            width: 100%;
            a {
                width: 100%;
            }
        }

        .header {
            display: flex;
            flex-direction: column;
            margin-bottom: 2.25rem;
            
            @media(min-width: $tablet) {
                margin-bottom: 4.5rem;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
            }
        }
        
        .grid {
            margin-bottom: 2.25rem;

            @media(max-width: calc($tablet - 1px)) {
                display: flex;
                flex-direction: row;
                width: 100%;
                gap: 1em;
                overflow: hidden;
                overflow-x: scroll;
                scroll-snap-type: x mandatory;
            }

            > li {
                width: auto;
                @media(max-width: calc($tablet - 1px)) {
                    scroll-snap-align: start;
                    flex: 0 0 calc(100% - 10vw);
                }
            }

            @media(min-width: $tablet) {
                display: grid;
                gap: 2rem;
                grid-template-columns: 1fr;
                grid-template-columns: repeat(2, 1fr);
            }

            @media(min-width: $desktop) {
                grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));
            }

            .tags button { 
                color: var(--sage);
                &:hover {
                    color: var(--orange-for-teal);
                }
            }
            
            .session-card {
                height: 100%;
                background-color: var(--teal-darkish);

                &:hover {
                    box-shadow: inset 0 0 0 1px var(--teal-outline);
                }

                .featured {
                    color: var(--teal-gray);
                }

                h3 {
                    font-size: calc(30 / var(--base-fs) * 1rem);
                    font-weight: 500;
                    line-height: 2.25rem;
                    letter-spacing: -0.0375rem;
                    margin-bottom: 2rem;
                    &:hover {
                        color: var(--orange-for-teal);
                    }
                }

                .speakers {
                    margin-bottom: 2rem;
                }

                .speaker-imgs {
                    img {
                        border-color: var(--teal-darkish);
                    }
                }

                .sponsored-by {
                    .logo {
                        background-color: white;
                        border-radius: var(--radius);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0.5rem 1.25rem;

                        .mess-image {
                            width: 7.5rem;
                        }
                    }
                }

            }
       }

    }
</style>